import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { Notification, calcDateSpanDescription, dateDiffMilliseconds } from 'model';
import { Link } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { useGetNotificationsQuery } from '../api/NotificationApi';
import { useGetProfileQuery } from '../api/UserApi';
import { Header } from '../components/Header';
import { NoResults } from '../components/NoResults';
import { ProfilePic } from '../components/ProfilePic';
import { IconChatbox, IconNotifications } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { QuirkLoader } from '../components/QuirkLoader';
import { useLoggedIn } from '../hooks/useLoggedIn';
// styles
// eslint-disable-next-line css-modules/no-unused-class
import notificationStyles from '../styles/notification.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import shareStyles from '../styles/Shared.module.css';
import { usePreferences } from '../hooks/usePreferences.js';
import { Badge } from '../components/Badge.js';

type NotificationListItemProps = {
	notification: Notification & { isNew: boolean };
	isLast?: boolean;
	isNew?: boolean;
};

function getNotificationHref(notification: Notification): string {
	if (notification.notificationType === 'claimBusiness') {
		return `/main/settings/admin/business/approvals`;
	} else if (
		notification.notificationType === 'submitBusiness' ||
		notification.notificationType === 'claimDenied' ||
		notification.notificationType === 'claimApproved' ||
		notification.notificationType === 'submitBusinessApproved' ||
		notification.notificationType === 'submitBusinessRejected'
	) {
		return `/main/bus/show/${notification.notificationDocument}`;
	}
	return `/main/latest/post/show/${notification.rootDocument}/expanded/${notification.notificationDocument}`;
}

const NotificationListItem = ({ notification, isLast }: NotificationListItemProps) => {
	const { data: user } = useGetProfileQuery(notification.notificationUser);
	return (
		<Link to={getNotificationHref(notification)} style={{ textDecoration: 'none' }}>
			<div
				className={(isLast
					? [notificationStyles.notificationListItem, notificationStyles.last]
					: [notificationStyles.notificationListItem]
				).join(' ')}
			>
				<ProfilePic user={user} size="xsmall" disableClick={true} />
				<div
					className={
						notification.isNew
							? [notificationStyles.notificationContent, notificationStyles.newItem].join(' ')
							: notificationStyles.notificationContent
					}
				>
					<div
						className={[shareStyles.name, user === undefined ? notificationStyles.emptyUser : ''].join(' ')}
					>
						{user?.username ?? ''}
					</div>
					<div className={notificationStyles.notificationBody}>
						<IconChatbox size="small" />
						<span className={notificationStyles.notificationMessage}>{notification.message}</span>
						<span>{'\u2022'}</span>
						<span className={shareStyles.time}>{calcDateSpanDescription(notification.created)}</span>
					</div>
				</div>
			</div>
		</Link>
	);
};

type NotificationsListProps = {
	notifications: (Notification & { isNew: boolean })[];
};

const NotificationsList = ({ notifications }: NotificationsListProps) => {
	return (
		<Virtuoso
			totalCount={notifications.length}
			data={notifications}
			itemContent={(index, item) => (
				<NotificationListItem notification={item} isLast={index === notifications.length - 1} />
			)}
		/>
	);
};

export const NotificationsPage = () => {
	const loggedIn = useLoggedIn();
	const query = useGetNotificationsQuery(undefined, { skip: !loggedIn.realUser });
	const { isLoading, data } = query;
	const [lastNotificationViewTime, setLastNotificationViewTime] = usePreferences('lastNotificationViewTime');
	const notifications: (Notification & { isNew: boolean })[] = (data ?? [])?.map(event => ({
		...event,
		isNew: !lastNotificationViewTime || dateDiffMilliseconds(lastNotificationViewTime, event.created) > 1
	}));
	const newCount = notifications?.filter(({ isNew }) => isNew).length ?? 0;

	useIonViewWillLeave(() => {
		if (notifications.length === 0) {
			return;
		}
		// check the list of notifications, save the latest notification timestamp as the 'new after' timestamp
		const latest = Math.max(...notifications.map(({ created }) => Date.parse(created)));

		setLastNotificationViewTime(new Date(latest).toISOString());
	}, [notifications]);

	useIonViewWillEnter(() => {
		if (loggedIn.realUser) {
			query.refetch();
		}
	}, [query.refetch, loggedIn.realUser]);

	return (
		<QuirkIonPage name="Notifications">
			<Header
				title="Notifications"
				right={<Badge type={newCount === 0 ? 'info' : 'alert'} content={newCount} />}
			/>
			<QuirkIonContent>
				<QuirkLoader
					isLoading={isLoading}
					loadingMessage="Loading feed..."
					data={notifications}
					noResults={
						<NoResults
							heading="Nothing here yet."
							subheading="Start commenting and posting and things will get busy in here."
							icon={<IconNotifications />}
						/>
					}
				>
					<NotificationsList notifications={notifications} />
				</QuirkLoader>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
